import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * CopyProtection Component
 * Implements copy protection for text and images across the site
 * - Disables right-click context menu on images
 * - Prevents text selection on public pages
 * - Disables common keyboard shortcuts for copying (Ctrl+C, Ctrl+X, Ctrl+S, etc.)
 * 
 * Admin pages are excluded from protection to allow normal operations
 */
const CopyProtection = () => {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');

  useEffect(() => {
    // Apply or remove copy-protect-text class based on current page
    const body = document.body;
    if (isAdminPage) {
      body.classList.remove('copy-protect-text');
    } else {
      body.classList.add('copy-protect-text');
    }

    // Cleanup on unmount
    return () => {
      body.classList.remove('copy-protect-text');
    };
  }, [isAdminPage]);

  useEffect(() => {
    // Skip protection on admin pages
    if (isAdminPage) {
      return;
    }

    // Prevent right-click context menu
    const handleContextMenu = (e) => {
      // Allow context menu on inputs and textareas
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      e.preventDefault();
      return false;
    };

    // Prevent keyboard shortcuts for copying/saving
    const handleKeyDown = (e) => {
      // Allow keyboard shortcuts in inputs and textareas
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Prevent Ctrl+C, Ctrl+X, Ctrl+S, Ctrl+U (copy, cut, save, view source)
      if (
        e.ctrlKey && 
        (e.key === 'c' || e.key === 'C' || 
         e.key === 'x' || e.key === 'X' || 
         e.key === 's' || e.key === 'S' ||
         e.key === 'u' || e.key === 'U')
      ) {
        e.preventDefault();
        return false;
      }

      // Prevent F12 (DevTools) - optional, can be removed if too restrictive
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }
    };

    // Prevent drag-and-drop of images
    const handleDragStart = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
        return false;
      }
    };

    // Prevent selection on images
    const handleSelectStart = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
        return false;
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('selectstart', handleSelectStart);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('selectstart', handleSelectStart);
    };
  }, [isAdminPage]);

  return null; // This component doesn't render anything
};

export default CopyProtection;
